// src/app/hooks/useConfirm.tsx
//
// Substitui window.confirm() nativo por um AlertDialog temático (shadcn),
// mantendo a mesma ergonomia imperativa: `if (await confirm('Apagar X?')) { ... }`.
// Um único <ConfirmDialogProvider/> é montado na raiz do app (App.tsx, ao
// lado do <Toaster/>) — qualquer módulo pode importar e chamar `confirm()`
// diretamente, sem precisar de hook/contexto local (mesmo padrão do `toast`
// do sonner).

import { useCallback, useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export interface ConfirmOptions {
  title?: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  /** 'destructive' usa botão vermelho — para exclusões e outras ações irreversíveis. */
  variant?: 'default' | 'destructive';
}

type ConfirmInput = ConfirmOptions | string;
type ConfirmFn = (input: ConfirmInput) => Promise<boolean>;

let confirmImpl: ConfirmFn | null = null;

function normalize(input: ConfirmInput): ConfirmOptions {
  return typeof input === 'string' ? { description: input } : input;
}

/**
 * Substituto drop-in de window.confirm(): `if (await confirm('Apagar X?'))`.
 * Requer <ConfirmDialogProvider/> montado na árvore (feito em App.tsx). Se por
 * algum motivo o provider não estiver montado, cai no confirm() nativo do
 * navegador — nunca trava a ação em silêncio.
 */
export function confirm(input: ConfirmInput): Promise<boolean> {
  if (!confirmImpl) {
    return Promise.resolve(window.confirm(normalize(input).description));
  }
  return confirmImpl(input);
}

export function ConfirmDialogProvider() {
  const [state, setState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const handleConfirm = useCallback<ConfirmFn>((input) => {
    const options = normalize(input);
    return new Promise<boolean>((resolve) => {
      setState({ options, resolve });
    });
  }, []);

  useEffect(() => {
    confirmImpl = handleConfirm;
    return () => {
      confirmImpl = null;
    };
  }, [handleConfirm]);

  const settle = (result: boolean) => {
    state?.resolve(result);
    setState(null);
  };

  const options = state?.options;

  return (
    <AlertDialog open={!!state} onOpenChange={(open) => { if (!open) settle(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{options?.title ?? 'Confirmar ação'}</AlertDialogTitle>
          <AlertDialogDescription>{options?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {options?.cancelText ?? 'Cancelar'}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => settle(true)}
            className={
              options?.variant === 'destructive'
                ? 'bg-red-600 hover:bg-red-700 text-white focus-visible:ring-red-300'
                : undefined
            }
          >
            {options?.confirmText ?? 'Confirmar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
